import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import {
  MonetizationOn,
  Checklist,
  PeopleAlt,
  Hotel,
  FlightTakeoff,
  Verified,
  DirectionsCar,
  Badge,
  Home,
  Edit,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  resolveProductLine,
  isUmrahLine,
  getServiceVisibility,
  getStayLocationChoices,
} from '../../../utils/productLineConfig';
import {
  umrahTransportOptions,
  leisureTransportOptions,
  carRentalClasses,
} from './transportOptions';

const nightlyRateByPackage = {
  '5 Star': 250,
  '4 Star': 180,
  '3 Star': 120,
  Economy: 90,
};

const transportCost = {
  'Private Car': 220,
  'High-Speed Train': 350,
  'Luxury Bus': 160,
  'Shared Transfer': 90,
  'Self Drive': 40,
};

const ziaraatCost = {
  Standard: 80,
  Premium: 140,
  Private: 220,
  None: 0,
};

const visaFees = {
  Tourist: 120,
  Umrah: 180,
  Visit: 100,
  Business: 150,
};

const carDailyRate = {
  Economy: 35,
  Sedan: 55,
  SUV: 85,
  Van: 95,
};

const PACKAGE_TYPES = ['5 Star', '4 Star', '3 Star', 'Economy'];

const QuantityRow = ({ label, value, onChange, min = 0, max = 20 }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography variant="body2" fontWeight={600}>{label}</Typography>
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <IconButton
        size="small"
        aria-label={`decrease ${label}`}
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
      >
        <Remove fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 700 }}>{value}</Typography>
      <IconButton
        size="small"
        aria-label={`increase ${label}`}
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
      >
        <Add fontSize="small" />
      </IconButton>
    </Stack>
  </Box>
);

const estimatedCostCardSx = {
  borderRadius: 2,
  background: 'linear-gradient(145deg, #0E1C2A 0%, #1F6F78 55%, #16555C 100%)',
  color: '#FFFFFF',
  '& .MuiTypography-root': { color: 'inherit' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.92)' },
  '& .MuiDivider-root': { borderColor: 'rgba(255,255,255,0.28)' },
};

const PriceSummaryStepV2 = ({ bookingData, priceCalculation, updateBookingData }) => {
  const productLine = resolveProductLine(bookingData);
  const isUmrah = isUmrahLine(bookingData);
  const vis = getServiceVisibility(productLine);
  const locationChoices = getStayLocationChoices(productLine, bookingData.destinationCountry);
  const transportOptions = isUmrah ? umrahTransportOptions : leisureTransportOptions;
  const visaOptions =
    productLine === 'Umrah' ? ['Umrah', 'Tourist', 'Visit'] : ['Tourist', 'Visit', 'Business'];

  const totalTravelers = (bookingData.adults || 0) + (bookingData.children || 0) + (bookingData.infants || 0);
  const includeHotel = bookingData.includeHotel !== false;
  const includeTransport = !!bookingData.includeTransport;
  const includeZiaraat = !!bookingData.includeZiaraat && isUmrah;
  const includeFlight = !!bookingData.includeFlight;
  const includeVisa = !!bookingData.includeVisa;
  const includeCar = !!bookingData.includeCarRental;
  const includeHomePickup = !!bookingData.includeHomePickup && includeTransport;

  const stayStops = [
    {
      includeKey: 'includeFirstEntry',
      locationKey: 'firstEntryLocation',
      nightsKey: 'firstEntryNights',
      defaultLocation: locationChoices[0],
    },
    {
      includeKey: 'includeSecondEntry',
      locationKey: 'secondEntryLocation',
      nightsKey: 'secondEntryNights',
      defaultLocation: locationChoices[1] || locationChoices[0],
    },
    {
      includeKey: 'includeThirdEntry',
      locationKey: 'thirdEntryLocation',
      nightsKey: 'thirdEntryNights',
      defaultLocation: locationChoices[2] || locationChoices[0],
    },
  ];

  const totalNights = includeHotel
    ? stayStops.reduce((sum, stop) => {
        if (!bookingData[stop.includeKey]) return sum;
        return sum + (bookingData[stop.nightsKey] || 0);
      }, 0)
    : 0;

  const patch = (data) => updateBookingData && updateBookingData(data);

  const computedFallback = useMemo(() => {
    const nightlyRate = nightlyRateByPackage[bookingData.packageType] || 120;
    const travelerFactor =
      (bookingData.adults || 0) + (bookingData.children || 0) * 0.6 + (bookingData.infants || 0) * 0.15;
    const baseStay = includeHotel ? nightlyRate * travelerFactor * totalNights : 0;
    const transport = includeTransport ? transportCost[bookingData.transportationType] || 120 : 0;
    const homePickup = includeHomePickup ? 45 : 0;
    const ziaraat = includeZiaraat ? ziaraatCost[bookingData.makkahZiaraat] || 0 : 0;
    const flight =
      includeFlight && bookingData.selectedFlightAmount
        ? Number(bookingData.selectedFlightAmount) || 0
        : includeFlight
          ? 350
          : 0;
    const visa = includeVisa ? (visaFees[bookingData.visaType] || 120) * Math.max(1, bookingData.adults || 1) : 0;
    const car =
      includeCar
        ? (carDailyRate[bookingData.carRentalClass] || 55) * Math.max(1, bookingData.carRentalDays || 1)
        : 0;
    const support = bookingData.onlineSupport ? 50 : 0;
    const subtotal = baseStay + transport + homePickup + ziaraat + flight + visa + car + support;
    const taxes = subtotal * 0.08;

    return {
      baseStay: Math.round(baseStay),
      transport: Math.round(transport),
      homePickup: Math.round(homePickup),
      ziaraat: Math.round(ziaraat),
      flight: Math.round(flight),
      visa: Math.round(visa),
      carRental: Math.round(car),
      support,
      taxes: Math.round(taxes),
      totalPrice: Math.round(subtotal + taxes),
    };
  }, [bookingData, totalNights, includeHotel, includeTransport, includeZiaraat, includeFlight, includeVisa, includeCar, includeHomePickup]);

  const summary = useMemo(() => {
    const calc = priceCalculation;
    const hasCalc = calc && typeof calc.totalPrice === 'number' && !Number.isNaN(calc.totalPrice);
    return hasCalc ? { ...computedFallback, ...calc } : computedFallback;
  }, [priceCalculation, computedFallback]);

  const displayCurrency = priceCalculation?.currency || (productLine === 'Domestic' || productLine === 'Local' ? 'PKR' : 'USD');
  const formatMoney = (val) =>
    typeof val === 'number'
      ? `${displayCurrency} ${val.toLocaleString()}`
      : '—';

  const applyBudgetPreset = (preset) => {
    if (!updateBookingData) return;
    switch (preset) {
      case 'value':
        updateBookingData({
          packageType: '3 Star',
          transportationType: 'Luxury Bus',
          makkahZiaraat: includeZiaraat ? 'Standard' : bookingData.makkahZiaraat,
          madinaZiaraat: includeZiaraat ? 'Standard' : bookingData.madinaZiaraat,
        });
        break;
      case 'economy':
        updateBookingData({
          packageType: 'Economy',
          transportationType: 'Luxury Bus',
          makkahZiaraat: includeZiaraat ? 'Standard' : bookingData.makkahZiaraat,
          madinaZiaraat: includeZiaraat ? 'Standard' : bookingData.madinaZiaraat,
          onlineSupport: false,
        });
        break;
      case 'shorten': {
        if (!includeHotel) break;
        const reducedFirst = bookingData.includeFirstEntry
          ? Math.max(2, (bookingData.firstEntryNights || 2) - 2)
          : bookingData.firstEntryNights;
        const reducedSecond = bookingData.includeSecondEntry
          ? Math.max(2, (bookingData.secondEntryNights || 2) - 2)
          : bookingData.secondEntryNights;
        const reducedThird = bookingData.includeThirdEntry
          ? Math.max(0, (bookingData.thirdEntryNights || 0) - 2)
          : bookingData.thirdEntryNights;
        updateBookingData({
          firstEntryNights: reducedFirst,
          secondEntryNights: reducedSecond,
          thirdEntryNights: reducedThird,
        });
        break;
      }
      default:
        break;
    }
  };

  const toggleService = (key) => {
    const next = !bookingData[key];
    const data = { [key]: next };
    if (key === 'includeTransport' && !next) data.includeHomePickup = false;
    if (key === 'includeFlight' && !next) {
      data.selectedFlightOfferId = null;
      data.selectedFlightAmount = null;
      data.selectedFlightCurrency = null;
      data.selectedFlightLabel = null;
    }
    patch(data);
  };

  const serviceChips = [
    includeFlight && 'Flight',
    includeHotel && 'Hotel',
    includeTransport && `Transport: ${bookingData.transportationType || '—'}`,
    includeHomePickup && 'Home pickup',
    includeVisa && `Visa (${bookingData.visaType || 'Tourist'})`,
    includeCar && 'Car rental',
    includeZiaraat && `Ziaraat: ${bookingData.makkahZiaraat || 'Standard'}`,
  ].filter(Boolean);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MonetizationOn color="primary" />
        Price Summary & Review
      </Typography>

      {!priceCalculation && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Calculating live pricing...
          </Typography>
          <LinearProgress color="primary" />
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checklist color="primary" />
                  Trip Overview
                </Typography>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2, gap: 1 }}>
                  <Chip label={productLine} color="primary" variant="outlined" />
                  <Chip label={`Departure: ${bookingData.departureDate || 'TBD'}`} />
                  <Chip label={`From: ${bookingData.flyFrom}`} />
                  {includeHotel && <Chip label={bookingData.packageType} />}
                  <Chip label={`Travelers: ${totalTravelers}`} icon={<PeopleAlt />} />
                  {serviceChips.map((label) => (
                    <Chip key={label} label={label} color="secondary" variant="outlined" size="small" />
                  ))}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Edit fontSize="small" color="action" />
                  Adjust your trip on this screen
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Travelers</Typography>
                      <QuantityRow
                        label="Adults"
                        value={bookingData.adults || 0}
                        min={1}
                        onChange={(v) => patch({ adults: v })}
                      />
                      <QuantityRow
                        label="Children"
                        value={bookingData.children || 0}
                        onChange={(v) => patch({ children: v })}
                      />
                      <QuantityRow
                        label="Infants"
                        value={bookingData.infants || 0}
                        onChange={(v) => patch({ infants: v })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Services</Typography>
                      <Stack spacing={0}>
                        {vis.showFlight && (
                          <FormControlLabel
                            control={<Switch size="small" checked={!!includeFlight} onChange={() => toggleService('includeFlight')} color="secondary" />}
                            label="Flight"
                          />
                        )}
                        {vis.showHotel && (
                          <FormControlLabel
                            control={<Switch size="small" checked={includeHotel} onChange={() => toggleService('includeHotel')} color="secondary" />}
                            label="Hotel / stay"
                          />
                        )}
                        {vis.showTransport && (
                          <FormControlLabel
                            control={<Switch size="small" checked={includeTransport} onChange={() => toggleService('includeTransport')} color="secondary" />}
                            label="Transport"
                          />
                        )}
                        {vis.showTransport && vis.showHomePickup && includeTransport && (
                          <FormControlLabel
                            control={<Switch size="small" checked={includeHomePickup} onChange={() => toggleService('includeHomePickup')} color="secondary" />}
                            label="Home pickup"
                          />
                        )}
                        {vis.showVisa && (
                          <FormControlLabel
                            control={<Switch size="small" checked={includeVisa} onChange={() => toggleService('includeVisa')} color="secondary" />}
                            label="Visa"
                          />
                        )}
                        {vis.showCarRental && (
                          <FormControlLabel
                            control={<Switch size="small" checked={includeCar} onChange={() => toggleService('includeCarRental')} color="secondary" />}
                            label="Car rental"
                          />
                        )}
                        {vis.showZiaraat && (
                          <FormControlLabel
                            control={<Switch size="small" checked={includeZiaraat} onChange={() => toggleService('includeZiaraat')} color="secondary" />}
                            label="Ziaraat"
                          />
                        )}
                      </Stack>
                    </Box>
                  </Grid>

                  {includeHotel && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700}>Hotel tier</Typography>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={bookingData.packageType || '3 Star'}
                            onChange={(_, val) => val && patch({ packageType: val })}
                          >
                            {PACKAGE_TYPES.map((p) => (
                              <ToggleButton key={p} value={p} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {p}
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                        </Stack>

                        {stayStops.map((stop, idx) => {
                          const included = !!bookingData[stop.includeKey];
                          const nights = bookingData[stop.nightsKey] || 0;
                          const location = bookingData[stop.locationKey] || stop.defaultLocation;
                          return (
                            <Box key={stop.includeKey} sx={{ mb: idx < stayStops.length - 1 ? 2 : 0 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={included}
                                      onChange={() => patch({ [stop.includeKey]: !included })}
                                      color="secondary"
                                    />
                                  }
                                  label={<Typography fontWeight={600}>{location || `Stop ${idx + 1}`}</Typography>}
                                />
                                {included && (
                                  <Stack direction="row" alignItems="center" spacing={0.25}>
                                    <IconButton
                                      size="small"
                                      onClick={() => patch({ [stop.nightsKey]: Math.max(1, nights - 1) })}
                                      disabled={nights <= 1}
                                    >
                                      <Remove fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2" fontWeight={700} sx={{ minWidth: 72, textAlign: 'center' }}>
                                      {nights} night{nights === 1 ? '' : 's'}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => patch({ [stop.nightsKey]: Math.min(14, nights + 1) })}
                                      disabled={nights >= 14}
                                    >
                                      <Add fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                )}
                              </Stack>
                              {included && locationChoices.length > 1 && (
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
                                  {locationChoices.map((city) => (
                                    <Chip
                                      key={`${stop.includeKey}-${city}`}
                                      size="small"
                                      label={city}
                                      color={location === city ? 'secondary' : 'default'}
                                      variant={location === city ? 'filled' : 'outlined'}
                                      onClick={() => patch({ [stop.locationKey]: city })}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}

                  {includeTransport && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Transport type"
                        value={bookingData.transportationType || transportOptions[0]?.value}
                        onChange={(e) => patch({ transportationType: e.target.value })}
                      >
                        {transportOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {includeVisa && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Visa type"
                        value={bookingData.visaType || visaOptions[0]}
                        onChange={(e) => patch({ visaType: e.target.value })}
                      >
                        {visaOptions.map((v) => (
                          <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {includeCar && (
                    <Grid item xs={12}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Car class"
                          value={bookingData.carRentalClass || 'Sedan'}
                          onChange={(e) => patch({ carRentalClass: e.target.value })}
                        >
                          {carRentalClasses.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                          ))}
                        </TextField>
                        <Box sx={{ minWidth: { sm: 160 } }}>
                          <QuantityRow
                            label="Rental days"
                            value={bookingData.carRentalDays || 1}
                            min={1}
                            max={30}
                            onChange={(v) => patch({ carRentalDays: v })}
                          />
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!bookingData.onlineSupport}
                          onChange={() => patch({ onlineSupport: !bookingData.onlineSupport })}
                          color="secondary"
                        />
                      }
                      label="24/7 online support"
                    />
                  </Grid>
                </Grid>

                {includeHotel && stayStops.every((stop) => !bookingData[stop.includeKey]) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    No stays selected.
                  </Typography>
                )}

                {includeHotel && (
                  <Stack direction="column" spacing={1} sx={{ mt: 2 }}>
                    {stayStops
                      .filter((stop) => bookingData[stop.includeKey])
                      .map((stop) => {
                        const location = bookingData[stop.locationKey] || stop.defaultLocation;
                        const nights = bookingData[stop.nightsKey] || 0;
                        return (
                          <Box key={stop.includeKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Hotel fontSize="small" color="secondary" />
                            <Typography variant="body1" fontWeight={600}>
                              {location} — {nights} nights
                            </Typography>
                          </Box>
                        );
                      })}
                  </Stack>
                )}

                {includeFlight && bookingData.selectedFlightLabel && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <FlightTakeoff fontSize="small" color="secondary" />
                    <Typography variant="body1" fontWeight={600}>
                      {bookingData.selectedFlightLabel}
                    </Typography>
                  </Box>
                )}

                {includeHotel && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Optimize for budget
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                      <Chip label="Value mix (3★ + bus)" onClick={() => applyBudgetPreset('value')} clickable />
                      <Chip label="Economy saver" onClick={() => applyBudgetPreset('economy')} clickable />
                      <Chip label="Shorten stay (-2N)" onClick={() => applyBudgetPreset('shorten')} clickable />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Quick tweaks to lower cost without leaving this step.
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={5}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <Card variant="outlined" sx={estimatedCostCardSx}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MonetizationOn />
                  Estimated Cost
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#FFFFFF' }}>
                  {formatMoney(summary.totalPrice)}
                </Typography>

                <Stack spacing={1.2}>
                  {includeHotel && (
                    <CostRow label="Stay" value={summary.baseStay} icon={<Hotel fontSize="small" />} formatAmount={formatMoney} />
                  )}
                  {includeFlight && (
                    <CostRow
                      label="Flight (indicative)"
                      value={summary.flight}
                      icon={<FlightTakeoff fontSize="small" />}
                      formatAmount={formatMoney}
                    />
                  )}
                  {includeTransport && (
                    <CostRow
                      label="Transport"
                      value={summary.transport}
                      icon={<DirectionsCar fontSize="small" />}
                      formatAmount={formatMoney}
                    />
                  )}
                  {includeVisa && (
                    <CostRow label="Visa" value={summary.visa} icon={<Badge fontSize="small" />} formatAmount={formatMoney} />
                  )}
                  {includeCar && (
                    <CostRow
                      label="Car rental"
                      value={summary.carRental}
                      icon={<DirectionsCar fontSize="small" />}
                      formatAmount={formatMoney}
                    />
                  )}
                  {includeZiaraat && (
                    <CostRow label="Ziaraat" value={summary.ziaraat} icon={<Verified fontSize="small" />} formatAmount={formatMoney} />
                  )}
                  {includeHomePickup && summary.homePickup > 0 && (
                    <CostRow
                      label="Home pickup"
                      value={summary.homePickup}
                      icon={<Home fontSize="small" />}
                      formatAmount={formatMoney}
                    />
                  )}
                  <CostRow label="Support" value={summary.support} icon={<Verified fontSize="small" />} formatAmount={formatMoney} />
                  <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.28)' }} />
                  <CostRow label="Taxes & Fees" value={summary.taxes} formatAmount={formatMoney} />
                </Stack>

                <Typography variant="body2" sx={{ mt: 2, opacity: 0.92, color: 'rgba(255,255,255,0.92)' }}>
                  Final price may adjust based on live availability. Flight fares are indicative until confirmed on the
                  IATA portal.
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

const CostRow = ({ label, value, icon, formatAmount }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#FFFFFF' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="body2" fontWeight={700} sx={{ color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight={700} sx={{ color: 'inherit' }}>
      {typeof value === 'number' ? (formatAmount ? formatAmount(value) : `$${value.toLocaleString()}`) : '—'}
    </Typography>
  </Box>
);

export default PriceSummaryStepV2;
